import React, { useState, useEffect } from 'react';
import { useFirebase } from '../../../context/FirebaseContext';
import { useAnalyticsWebhookEnabled } from '../../../context/FeatureFlagContext';
import { useAnalytics } from '../../../context/AnalyticsContext';
import { updateWebhookEnabled, regenerateWebhookSecret } from '../../../api/analytics';
import { getApiOrigin } from '../../../api/core';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import WebhookIcon from '@mui/icons-material/Webhook';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import './PipelineSettingsModal.css';

/**
 * AnalyticsPipelineSettingsModal - settings modal for analytics pipelines
 * Similar to PipelineSettingsModal for validation pipelines
 */
const AnalyticsPipelineSettingsModal = ({ pipeline, open, onClose }) => {
  const { auth } = useFirebase();
  const { loadPipelines } = useAnalytics();
  const webhookFeatureEnabled = useAnalyticsWebhookEnabled();

  const [webhookEnabled, setWebhookEnabled] = useState(pipeline?.webhookEnabled || false);
  const [webhookUrl, setWebhookUrl] = useState(pipeline?.webhookUrl || '');
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookError, setWebhookError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  // URL query parameter options
  const [includeDetails, setIncludeDetails] = useState(false);

  // Reset state when pipeline changes
  useEffect(() => {
    if (pipeline) {
      setWebhookEnabled(pipeline.webhookEnabled || false);
      setWebhookUrl(pipeline.webhookUrl || '');
    }
  }, [pipeline]);

  if (!open || !pipeline) return null;

  const handleToggleWebhook = async () => {
    if (webhookLoading) return;
    setWebhookLoading(true);
    setWebhookError(null);

    try {
      await updateWebhookEnabled(auth, (result) => {
        if (result.error) {
          setWebhookError(result.error);
        } else {
          setWebhookEnabled(result.webhookEnabled);
          setWebhookUrl(result.webhookUrl);
          if (loadPipelines) loadPipelines();
        }
      }, pipeline.id, !webhookEnabled);
    } catch (e) {
      setWebhookError(e.message || 'Failed to update webhook settings');
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleRegenerateSecret = async () => {
    if (webhookLoading) return;
    if (!window.confirm('Are you sure you want to regenerate the webhook secret? This will invalidate the current URL.')) {
      return;
    }
    setWebhookLoading(true);
    setWebhookError(null);

    try {
      await regenerateWebhookSecret(auth, (result) => {
        if (result.error) {
          setWebhookError(result.error);
        } else {
          setWebhookUrl(result.webhookUrl);
          if (loadPipelines) loadPipelines();
        }
      }, pipeline.id);
    } catch (e) {
      setWebhookError(e.message || 'Failed to regenerate secret');
    } finally {
      setWebhookLoading(false);
    }
  };

  const getFullWebhookUrl = (withQueryParams = true) => {
    if (!webhookUrl) return '';
    // If backend returns relative URL, prepend the API origin
    let url = webhookUrl.startsWith('/') ? `${getApiOrigin()}${webhookUrl}` : webhookUrl;
    if (withQueryParams && includeDetails) {
      url += '?details=true';
    }
    return url;
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(getFullWebhookUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setWebhookError('Failed to copy to clipboard');
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="pipeline-settings-overlay" onClick={handleOverlayClick}>
      <div className="pipeline-settings-modal">
        <div className="pipeline-settings-header">
          <h3>Analytics Pipeline Settings</h3>
          <button className="pipeline-settings-close" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="pipeline-settings-content">
          {/* Pipeline Info */}
          <div className="pipeline-settings-section">
            <label className="pipeline-settings-label">Pipeline</label>
            <div className="pipeline-info-display">
              <span className="pipeline-info-name">{pipeline.name}</span>
              <span className="pipeline-info-instrument">{pipeline.instrument}</span>
            </div>
          </div>

          {/* Webhook Section */}
          {webhookFeatureEnabled && (
            <div className="pipeline-settings-section webhook-section">
              <div className="webhook-header">
                <WebhookIcon className="webhook-icon" />
                <label className="pipeline-settings-label">Webhook Integration</label>
                <button
                  className="webhook-help-btn"
                  onClick={() => setShowHelp(!showHelp)}
                  title="Help"
                >
                  <HelpOutlineIcon fontSize="small" />
                </button>
              </div>

              {webhookError && <div className="pipeline-settings-error">{webhookError}</div>}

              <div className="webhook-toggle-row">
                <span className="webhook-toggle-label">
                  {webhookEnabled ? 'Webhook enabled' : 'Webhook disabled'}
                </span>
                <button
                  className={`webhook-toggle ${webhookEnabled ? 'enabled' : ''}`}
                  onClick={handleToggleWebhook}
                  disabled={webhookLoading}
                >
                  <span className="webhook-toggle-slider" />
                </button>
              </div>

              {webhookEnabled && (
                <div className="webhook-url-section">
                  <label className="webhook-url-label">Webhook URL</label>
                  <div className="webhook-url-row">
                    <input
                      type="text"
                      value={getFullWebhookUrl()}
                      readOnly
                      className="webhook-url-input"
                    />
                    <button
                      className={`webhook-url-btn ${copied ? 'copied' : ''}`}
                      onClick={handleCopyUrl}
                      title="Copy URL"
                    >
                      {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                    </button>
                    <button
                      className="webhook-url-btn regenerate"
                      onClick={handleRegenerateSecret}
                      disabled={webhookLoading}
                      title="Regenerate secret"
                    >
                      <RefreshIcon fontSize="small" />
                    </button>
                  </div>

                  {/* URL Options */}
                  <div className="webhook-options">
                    <label className="webhook-option">
                      <input
                        type="checkbox"
                        checked={includeDetails}
                        onChange={(e) => setIncludeDetails(e.target.checked)}
                      />
                      <span className="webhook-option-text">
                        <strong>details</strong> - Include detailed module execution results
                      </span>
                    </label>
                  </div>

                  <p className="webhook-url-hint">
                    Keep this URL secret. Use it to run market analysis from external systems.
                  </p>
                </div>
              )}

              {showHelp && (
                <div className="webhook-help-card">
                  <h4>How to use the Analytics Webhook</h4>
                  <p>Send a POST request to run market analysis. Each module uses its own configured timeframe.</p>
                  <pre className="webhook-help-code">
{`POST ${getFullWebhookUrl() || '/api/v2/webhook/analytics/{id}/{secret}'}
Content-Type: application/json

{}`}
                  </pre>
                  <p>Response:</p>
                  <pre className="webhook-help-code">
{`{
  "success": true,
  "direction": "UP",        // UP, DOWN, or NEUTRAL
  "certainty": 0.72,        // Confidence 0.0 - 1.0
  "strength": 0.65,         // Vote margin
  "upVotes": 5,
  "downVotes": 3,
  "neutralVotes": 0,
  "instrument": "${pipeline.instrument}",
  "pipelineName": "${pipeline.name}",
  "remainingCalls": 95
}`}
                  </pre>
                  <p>
                    <strong>Options:</strong><br/>
                    <code>?details=true</code> - Include detailed module execution results
                  </p>
                  <p>
                    <strong>Rate Limits:</strong><br/>
                    Free: 10/day | Basic: 100/day | Pro: 1,000/day | Unlimited: No limit
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPipelineSettingsModal;
